// Results.js
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Rating,
  Typography,
  Fade
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import confetti from 'canvas-confetti';
import { useGameData } from '../hooks/useGameData';
import { useQuizRating } from '../hooks/useQuizRating';
import { Podium } from '../components/Podium';
import { RankingList } from '../components/RankingList';
import { RatingDialog } from '../components/RatingDialog';

const Results = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [openRatingDialog, setOpenRatingDialog] = useState(false);

  // Custom hooks
  const { gameData, error, loading, finalResults } = useGameData(gameId, currentUser);
  const {
    rating,
    setRating,
    feedback,
    setFeedback,
    hasRated,
    submitting,
    submitRating
  } = useQuizRating(gameData?.currentQuiz?.quizId, currentUser);

  // Confetti effect
  useEffect(() => {
    if (!gameData || loading) return;

    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    let confettiInterval;

    confettiInterval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(confettiInterval);
        return;
      }

      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#FFD700', '#FFA500', '#FF4500']
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#FFD700', '#FFA500', '#FF4500']
      });
    }, 150);

    return () => {
      if (confettiInterval) {
        clearInterval(confettiInterval);
      }
    };
  }, [gameData, loading]);

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}>
          <Paper
            className="glass"
            sx={{
              p: 3,
              background: 'rgba(28, 39, 60, 0.4)',
              backdropFilter: 'blur(10px)',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <Typography color="error" variant="h6">
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{ mt: 2 }}
            >
              Zurück zur Startseite
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (!gameData || !finalResults) return null;

  const handleRatingSubmit = async () => {
    await submitRating();
    setOpenRatingDialog(false);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Paper
          className="glass"
          sx={{
            p: 4,
            mb: 4,
            background: 'rgba(28, 39, 60, 0.4)',
            backdropFilter: 'blur(10px)',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 2
          }}
        >
          {/* Sieger-Sektion */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            mb: 4
          }}>
            <Typography
              variant="h5"
              sx={{
                color: '#FFD700',
                textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              🏆 Champions
            </Typography>
          </Box>

          {/* Podium-Komponente */}
          <Podium winners={finalResults.slice(0, 3)} />

          {/* Ranglisten-Sektion */}
          <Fade in={true}>
            <Paper
              className="glass"
              elevation={2}
              sx={{
                p: 3,
                mt: 4,
                background: 'rgba(28, 39, 60, 0.4)',
                backdropFilter: 'blur(10px)',
                borderColor: 'rgba(255, 255, 255, 0.1)'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  textAlign: 'center',
                  mb: 3,
                  fontWeight: 'bold',
                  color: 'var(--color-secondary)',
                  opacity: 0.9
                }}
              >
                Rangliste
              </Typography>

              {/* Ranglisten-Komponente */}
              <RankingList
                players={finalResults}
                currentUserId={currentUser.uid}
              />
            </Paper>
          </Fade>

          {/* Bewertungs-Sektion */}
          {!hasRated && (
            <Box sx={{
              mt: 4,
              textAlign: 'center',
              p: 3,
              background: 'rgba(28, 39, 60, 0.2)',
              borderRadius: 2
            }}>
              <Typography
                variant="h6"
                sx={{ mb: 2 }}
              >
                Wie hat dir das Quiz gefallen?
              </Typography>
              <Rating
                value={rating}
                onChange={(_, newValue) => {
                  setRating(newValue);
                  if (newValue) {
                    setOpenRatingDialog(true);
                  }
                }}
                size="large"
                sx={{
                  '& .MuiRating-icon': {
                    color: 'var(--color-secondary)',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.2)'
                    }
                  }
                }}
              />
            </Box>
          )}

          {/* Aktionen */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4
          }}>
            <Button
              variant="contained"
              onClick={() => navigate(`/lobby/${gameId}`)}
              startIcon={<ArrowBack />}
              className="button-glow"
              sx={{
                minWidth: 200,
                background: 'var(--color-secondary)',
                color: 'var(--color-text-primary)',
                '&:hover': {
                  background: 'var(--color-secondary-light)'
                }
              }}
            >
              Zurück zur Lobby
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Bewertungs-Dialog */}
      <RatingDialog
        open={openRatingDialog}
        onClose={() => setOpenRatingDialog(false)}
        rating={rating}
        feedback={feedback}
        onFeedbackChange={(e) => setFeedback(e.target.value)}
        onSubmit={handleRatingSubmit}
        submitting={submitting}
      />
    </Container>
  );
};

export default Results;